import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  InputGroup,
  Row,
} from 'react-bootstrap';
import MaterialModels, {
  BayList,
  MaterialType,
} from '../../constants/MaterialModels';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import startCase from 'lodash/startCase';
import './MaterialSettings.css';
import { withTranslation } from 'react-i18next';
import {
  FieldArray,
  Formik,
} from 'formik';
import logger from '../../utils/logger';
import { DeviceTypes } from '../../constants/DeviceModels';
import { toFixed } from '../../utils/numbers';
import Tabs from 'react-bootstrap/lib/Tabs';
import Tab from 'react-bootstrap/lib/Tab';
import AuditLog from '../../components/AuditLog/AuditLog';
import { toModel } from '../../utils/json-mapper/toModel';
import { PERMISSION } from '../../modules/newAuth/model/User';
import { showConfirmModal } from '../../modules/modals/confirmService';
import { ConfirmWithInitials } from '../../components/Confirm/ConfirmWithInitials';
import { BatchEventType } from '../../constants/BatchEventType';
import InfoTooltip from '../../components/InfoTooltip';
import Filters from '../../components/Filters/Filters';

class MaterialSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      material: {},
      error: null,
      errors: {},
      loading: true,
      saving: false,
      unassigning: false,
      changeConfirmed: false,
      initialsEntered: false,
      filter: '',
    };
    this.anchor = React.createRef();
  }

  componentWillMount() {
    const { materialStore, editingMaterial, devicesStore } = this.props;
    const model = MaterialModels[editingMaterial.assignment];

    if (editingMaterial.assignment !== MaterialType.None) {
      const details = materialStore.materialDetails[editingMaterial.materialDetailId] || {};
      let material = {};

      Object.keys(model).forEach(fieldName => {
        material[fieldName] = details[fieldName] || model[fieldName].defaultValue;
      });

      this.setState({
        material,
        loading: false,
      });

    } else {
      this.setState({
        material: {
          assignment: editingMaterial.assignment,
        },
        loading: false,
      });
    }

    if (!devicesStore.devices || !devicesStore.devices.length) {
      devicesStore.loadDevices();
    }
  }

  componentDidMount () {
    this.anchor.current.scrollIntoView();
    this.mounted = true;
  }

  componentWillUnmount () {
    this.mounted = false;
  }

  isFieldValid(fieldName, values) {
    const { editingMaterial } = this.props;
    const model = MaterialModels[editingMaterial.assignment] || {};

    if (!model[fieldName]) {
      return true;
    } else if (model[fieldName].required && !values[fieldName]) {
      return false;
    } else if (!isNaN(model[fieldName].min) && Number(values[fieldName]) < model[fieldName].min) {
      return false;
    } else if (isNaN(model[fieldName].min) && Number(values[fieldName]) < Number(values[model[fieldName].min])) {
      return false;
    } else if (!isNaN(model[fieldName].max) && Number(values[fieldName]) > model[fieldName].max) {
      return false;
    } else if (isNaN(model[fieldName].max) && Number(values[fieldName]) > Number(values[model[fieldName].max])) {
      return false;
    }

    return true;
  }

  isValid(values) {
    const { editingMaterial } = this.props;
    const model = MaterialModels[editingMaterial.assignment] || {};
    let valid = true;

    this.setState({ error: null, errors: {}, saving: true });

    Object.keys(model).forEach(fieldName => {
      if (!this.isFieldValid(fieldName, values)) {
        valid = false;
      }
    });

    if (!valid) {
      this.setState({ error: 'Please fill in all required fields!', saving: false });
    }

    return valid;
  }

  hasBinChanged(values) {
    const { editingMaterial } = this.props;
    let binHasChanged = false;

    editingMaterial.bins?.forEach(bin => {
      if (!values.bins?.find(b => Number(b.binId) === Number(bin.binId))) {
        binHasChanged = true;
      }
    });

    return binHasChanged;
  }

  onSave(values) {
    const { initialsEntered, changeConfirmed } = this.state;
    const { editingMaterial, materialStore, batchStore, locationsStore, t } = this.props;
    const { currentPlant } = locationsStore;
    let promise = Promise.resolve();

    if (!this.isValid(values)) {
      return;
    }

    const renderMessage = () => {
      const name = materialStore.getMaterialName(editingMaterial);
      return (
        <div>
          <p>
            {t(`This Material ${name} has been changed for`)} {batchStore.currentTenantSlug?.toUpperCase()} {currentPlant.code} {currentPlant.description}
          </p>
          <p>
            {t('Are you sure you want to save this change?')}
          </p>
        </div>
      );
    };

    if (this.hasBinChanged(values) && !changeConfirmed) {
      showConfirmModal({
        title: `${t('The Bin in Material has been changed')}`,
        message: renderMessage(),
        okLabel: t('Yes'),
        cancelLabel: t('No'),
        enableEscape: false,
      })
        .then(() => {
          this.setState({ changeConfirmed: true });
          setTimeout(() => {
            this.onSave(values);
          }, 1000);
        })
        .catch(() => {
          if (this.mounted) {
            this.setState({ initialsEntered: false, changeConfirmed: false });
          }
        })
        .finally(() => {
          if (this.mounted) {
            this.setState({ saving: false });
          }
        });
      return;
    }

    if (this.hasBinChanged(values) && changeConfirmed && !initialsEntered) {
      const onIOConfirm = (e) => {
        this.setState({ initialsEntered: true });
        values.lastTested = {
          eventType: BatchEventType.IO_CHANGE,
          author: e.target.value,
          time: new Date().toISOString(),
        };
      };

      showConfirmModal({
        title: t('Confirm the change'),
        message: <ConfirmWithInitials onConfirm={onIOConfirm} />,
        okLabel: t('Yes'),
        cancelLabel: t('No'),
        enableEscape: false,
      })
        .then(() => {
          setTimeout(() => {
            this.onSave(values);
          }, 1000);
        })
        .catch(() => {
          if (this.mounted) {
            this.setState({ initialsEntered: false, changeConfirmed: false });
          }
        })
        .finally(() => {
          if (this.mounted) {
            this.setState({ saving: false });
          }
        });
      return;
    }

    if (editingMaterial.assignment === MaterialType.None) {
      promise = materialStore.assignMaterial(
        {...editingMaterial, ...values},
        values.assignment,
      );
    } else {
      promise = materialStore.saveMaterialDetail(editingMaterial, values, false, false);
    }

    promise
      .then(() => {
        this.props.cancelEdit();
      })
      .catch(err => {
        if (this.mounted) {
          this.setState({ error: err.message, errors: err.errors || {} });
        }
      })
      .finally(() => {
        if (this.mounted) {
          this.setState({ saving: false });
        }
      });
  };

  unassign = () => {
    const { editingMaterial, materialStore, cancelEdit, t } = this.props;

    showConfirmModal({
      title: t('Are you sure you want to unassign the material?'),
      message: t('When you unassign, the material will be removed from the list and all devices will be disconnected from it'),
      okLabel: t('Yes'),
      cancelLabel: t('No'),
      enableEscape: false,
    }).then(() => {
      if (this.mounted) {
        this.setState({ unassigning: true });
      }
      materialStore
        .unassignMaterial(editingMaterial)
        .then(() => {
          cancelEdit();
        })
        .catch(err => {
          if (this.mounted) {
            this.setState({ error: err.message, unassigning: false });
          }
        });
    })
      .catch(() => {
        logger.log(`unassign material: ${editingMaterial?.code}? user clicked No`);
      });

  };

  renderArray(name, values, config, handleChange) {
    const { devicesStore, newAuthStore, t } = this.props;
    const { bins } = devicesStore;
    const canEditDevices = newAuthStore.user?.hasAccess(PERMISSION.BATCH_DEVICE);

    if (!Array.isArray(values[name])) {
      values[name] = [];
    }

    values[name] = values[name].map(item => toModel(config.defaultValue, item));

    const isHidden = (prop) => {
      const { settingsStore } = this.props;
      return (prop === 'bay' && settingsStore.configuration && !settingsStore.configuration.useBay) || shouldHideSplit(prop);
    };

    const isCheckbox = prop => {
      return ['isActive', 'isPrimary', 'isVisible'].includes(prop);
    };

    const shouldHideSplit = (prop) => {
      const bins = devicesStore.getActiveBinsForMultiScale(values, true);
      return prop === 'split' && !bins;
    };

    const handleDuplicateBin = async (index, arrayHelpers) => {
      const item = values[name][index];
      const targetBinId = Number(item.binId);
      const bin = bins.find(b => b.id === targetBinId);

      if (!bin) {
        throw new Error(`Bin ${item.binId} not found`);
      }

      try {
        const duplicatedBin = await devicesStore.confirmDuplicateDevice(bin, DeviceTypes.Bin);
        if (!duplicatedBin) {
          return;
        }
        arrayHelpers.push({
          ...item,
          isPrimary: false,
          binId: duplicatedBin.id,
        });
        devicesStore.cancelEdit();
      } catch (error) {
        logger.error('Error duplicating item', error);
      }
    };

    return (
      <React.Fragment>
        <FieldArray
          name={name}
          render={arrayHelpers => (
            <div className="field-array">
              {values[name] && values[name].length ? (
                values[name].map((item, index) => (
                  <Row key={index}>
                    {item && Object.keys(item).map((prop, i) => (
                      !isHidden(prop) && (
                        <Col key={i}
                          sm={isCheckbox(prop) ? 1 : 2}
                          xs={isCheckbox(prop) ? 1 : 2}>
                          <FormGroup>
                            <ControlLabel>{t(startCase(prop))}</ControlLabel>
                            {isCheckbox(prop)
                              ? (
                                <Checkbox
                                  inline
                                  name={`${name}.${index}.${prop}`}
                                  value={values[name][index][prop]}
                                  checked={!!values[name][index][prop]}
                                  onChange={handleChange}
                                />
                              )
                              :
                              prop === 'split'
                                ? (
                                  <FormControl
                                    name={`${name}.${index}.${prop}`}
                                    type={'number'}
                                    value={values[name][index][prop] || ''}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onChange={handleChange}
                                  />
                                )
                                : (
                                  <FormControl
                                    name={`${name}.${index}.${prop}`}
                                    componentClass="select"
                                    value={values[name][index][prop] || ''}
                                    onChange={handleChange}
                                  >
                                    <option value="">- {t('Select')} -</option>
                                    {(prop === 'binId' || prop === 'linkedBinId') &&
                                      bins && bins.map((option, i) => (
                                      <option key={i} value={option.id}>
                                        {option.description}
                                      </option>
                                    ))
                                    }
                                    {prop === 'bay' &&
                                Object.keys(BayList).map((option, i) => (
                                  <option key={i} value={BayList[option]}>
                                    {option}
                                  </option>
                                ))
                                    }
                                  </FormControl>
                                )}
                          </FormGroup>
                        </Col>
                      )

                    ))}
                    <Col sm={2} xs={2}>
                      <ControlLabel>{t('Balance')}</ControlLabel>
                      <div>
                        {toFixed((values.deviceBalance.find(v => v.deviceId === item.binId) || {}).currentBalance || 0, 2)}
                        {values.deviceBalance.find(v => v.deviceId === item.linkedBinId) && (
                          <>{' + '}
                            {toFixed((values.deviceBalance.find(v => v.deviceId === item.linkedBinId) || {}).currentBalance || 0, 2)}
                          </>
                        )}
                        {' '}{values.itemInventoryUnitSymbol}
                      </div>
                    </Col>
                    {canEditDevices && (
                      <Col sm={3} xs={3}>
                        <ControlLabel>{t('Options')}</ControlLabel>
                        <div>
                          <Button className="btn btn-sm btn-default"
                            title={item.binId ? t('Edit') : t('Create')} onClick={() => devicesStore.startEdit(item.binId, DeviceTypes.Bin)}>
                            <i className="fa fa-pencil"/>
                          </Button>{' '}
                          <Button className="btn btn-sm btn-default"
                            title={t('Remove')}
                            onClick={() => arrayHelpers.remove(index)}>
                            <i className="fa fa-remove"/>
                          </Button>{' '}
                          <Button className="btn btn-sm btn-default"
                            title={t('Duplicate')}
                            disabled={!item.binId}
                            onClick={() => handleDuplicateBin(index, arrayHelpers)}>
                            <i className="fa fa-copy"/>
                          </Button>
                        </div>
                      </Col>
                    )}
                  </Row>
                ))
              ) : '' }

              <Row>
                <Col sm={3}>
                  <a className="btn btn-sm btn-default btn-add"
                    title={t('Add')} onClick={() => arrayHelpers.push(config.defaultValue)}>
                    <i className="fa fa-plus"/>
                  </a>
                </Col>
              </Row>
            </div>
          )}
        />
      </React.Fragment>
    );
  }

  getProportion(name, values) {
    const value = values[name];
    return 100 - value;
  }

  getValue(name, values, config) {
    const { difference, sum } = config;

    if (difference.length === 2) {
      return toFixed(values[difference[0]] - (values[difference[1]] || 0), 2);
    }

    if (sum.length === 2) {
      return toFixed(values[sum[0]] + (values[sum[1]] || 0), 2);
    }

    return values[name];
  }

  renderField(name, values, config, handleChange) {
    const { t } = this.props;
    const { type, disabled, options, required, min, max, step, maxlength } = config;
    const value = this.getValue(name, values, config);

    switch (type) {
      case 'number':
      case 'text':
        return (
          <FormControl
            name={name}
            type={type}
            placeholder={type === 'number' ? 0 : ''}
            value={value || ''}
            disabled={disabled}
            required={required}
            min={min}
            max={max}
            maxLength={maxlength}
            step={step}
            onChange={handleChange}
          />
        );
      case 'boolean':
        return (
          <Checkbox
            inline
            name={name}
            value={value}
            checked={!!value}
            onChange={handleChange}
          />
        );
      case 'array':
        return (this.renderArray(name, values, config, handleChange));
      default:
        return (
          <FormControl
            name={name}
            componentClass="select"
            disabled={disabled}
            required={required}
            value={value || ''}
            onChange={handleChange}
          >
            <option value="">- {t('Select')} -</option>
            {options && options.map((option, i) => (
              <option key={i} value={option.value || option.id}>
                {
                  config.option
                    ? option[config.option] || option.name
                    : option.name || option.text
                }
              </option>
            ))}
          </FormControl>
        );
    }
  }

  getUnit(material = {}, unit = '') {
    if (!unit) {
      return '';
    }

    return material[unit] || unit;
  }

  getFieldConfig = fieldName => {
    const { editingMaterial, materialStore, devicesStore } = this.props;
    const { material } = this.state;
    const model = MaterialModels[editingMaterial.assignment];
    const fieldConfig = model[fieldName];

    if (typeof fieldConfig === 'string') {
      return {
        type: fieldConfig,
        label: startCase(fieldName),
        col: 4,
        disabled: false,
        options: materialStore[fieldConfig] || devicesStore[fieldConfig] || [],
        required: false,
        min: 0.0,
        max: null,
        step: 0.1,
      };
    }

    return {
      group: fieldConfig.group,
      type: fieldConfig.type || 'text',
      label: fieldConfig.label || startCase(fieldName),
      hideLabel: fieldConfig.hideLabel || false,
      col: fieldConfig.col || 4,
      disabled: fieldConfig.disabled || false,
      dependsOn: fieldConfig.dependsOn,
      dependsOnOneOf: fieldConfig.dependsOnOneOf || [],
      hideOn: fieldConfig.hideOn,
      unit: fieldConfig.unit || '',
      option: fieldConfig.option || '',
      options: materialStore[fieldConfig.type] || devicesStore[fieldConfig.type] || [],
      required: fieldConfig.required || false,
      min: fieldConfig.min && isNaN(fieldConfig.min) ? material[fieldConfig.min] : fieldConfig.min || 0,
      max: fieldConfig.max && isNaN(fieldConfig.max) ? material[fieldConfig.max] : fieldConfig.max || null,
      maxlength: fieldConfig.maxlength || 100000,
      step: fieldConfig.step || 0.1,
      hidden: fieldConfig.hidden,
      defaultValue: fieldConfig.defaultValue,
      proportion: fieldConfig.proportion || false,
      tooltip: fieldConfig.tooltip,
      difference: fieldConfig.difference || [],
      sum: fieldConfig.sum || [],
    };

  };

  shouldRender(fieldName, values, fieldConfig) {
    const { editingMaterial } = this.props;

    if (fieldConfig.hidden) {
      return false;
    }

    if (fieldConfig.dependsOn && fieldConfig.dependsOnOneOf && fieldConfig.dependsOnOneOf.length) {
      return fieldConfig.dependsOnOneOf.includes(values[fieldConfig.dependsOn]);
    }

    if (fieldConfig.dependsOn) {
      return !!values[fieldConfig.dependsOn];
    }

    if (fieldConfig.hideOn) {
      return !editingMaterial[fieldConfig.hideOn];
    }

    return true;
  }

  renderGroup(group) {
    return (
      <Col sm={12}>
        <h3 className="group-settings">{group}</h3>
      </Col>
    );
  }

  renderFields(fieldName, values, handleChange) {
    const { t } = this.props;
    const { errors } = this.state;
    const fieldConfig = this.getFieldConfig(fieldName);

    return !this.shouldRender(fieldName, values, fieldConfig) ? '' : (
      <Col sm={fieldConfig.col} xs={12} key={fieldName} className={!this.isFieldValid(fieldName, values) || errors[fieldName] ? 'has-error' : ''}>
        {!fieldConfig.hideLabel && (
          <ControlLabel>
            {t(fieldConfig.label)}
            {fieldConfig.tooltip && <InfoTooltip title={t(fieldConfig.label)} text={t(fieldConfig.tooltip)} />}
          </ControlLabel>
        )}
        {!fieldConfig.unit
          ? this.renderField(fieldName, values, fieldConfig, handleChange)
          : (
            <FormGroup>
              <InputGroup>
                {fieldConfig.proportion && (
                  <InputGroup.Addon>
                    {this.getProportion(fieldName, values, fieldConfig)}
                  </InputGroup.Addon>
                )}
                {this.renderField(fieldName, values, fieldConfig, handleChange)}
                <InputGroup.Addon>
                  {Array.isArray(fieldConfig.unit)
                    ? `${values[fieldConfig.unit[0]]} / ${fieldConfig.unit[1]}`
                    : this.getUnit(values, fieldConfig.unit)
                  }
                </InputGroup.Addon>
              </InputGroup>
            </FormGroup>
          )
        }
      </Col>
    );
  }

  handleFilterChange = (e) => {
    const filter = e.target.value;
    this.setState({ filter });
  };

  renderForm() {
    const { editingMaterial, cancelEdit, t } = this.props;
    const { loading, saving, unassigning, error, material, filter } = this.state;
    const model = editingMaterial ? MaterialModels[editingMaterial.assignment] : null;

    if (loading || !editingMaterial) {
      return (
        <h5 className="text-center">
          <i className="fa fa-circle-o-notch fa-spin" /> {t('Loading...')}
        </h5>
      );
    }

    return (
      <Formik
        onSubmit={(values) => {
          this.onSave(values);
        }}
        initialValues={{
          ...material,
        }}
      >{({
          handleSubmit,
          handleChange,
          values }) => (
          <Form className="material-settings-form">
            {(editingMaterial.assignment === MaterialType.Weighed || editingMaterial.assignment === MaterialType.Metered) && (
              <Row className="form-fields">
                <Col sm={4}>
                  <FormGroup>
                    <FormControl
                      type="text"
                      value={filter}
                      placeholder={t('Search')}
                      onChange={this.handleFilterChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
            <Row className="form-fields">
              <Filters filter={filter} data={model} keys={['label']} renderData={(data) => (
                <>
                  {Object.keys(data).map((fieldName, index, fields) => {
                    const currentField = data[fieldName];
                    const previousField =
                      index > 0 ? data[fields[index - 1]] : null;

                    const isNewGroup = currentField?.group !== previousField?.group;

                    return (
                      <React.Fragment key={fieldName}>
                        {isNewGroup && this.renderGroup(currentField?.group)}

                        {this.renderFields(fieldName, values, handleChange)}
                      </React.Fragment>
                    );
                  })}
                </>
              )}/>
            </Row>
            {!!error && <Alert bsStyle="danger">{error}</Alert>}
            <Row className="form-buttons">
              <Col sm={12} className="text-right">
                {(editingMaterial.assignment === MaterialType.Weighed || editingMaterial.assignment === MaterialType.Metered) && (
                  <LoadingButton
                    bsStyle="danger"
                    className="pull-left"
                    type="button"
                    label={t('Unassign')}
                    loadingLabel={t('Unassign')}
                    onClick={this.unassign}
                    isLoading={unassigning}
                  />
                )}

                <Button type="button" onClick={cancelEdit}>
                  {t('Close')}
                </Button>

                <LoadingButton
                  bsStyle="primary"
                  type="submit"
                  label={t('Save')}
                  loadingLabel={t('Saving')}
                  onClick={handleSubmit}
                  isLoading={saving}
                />
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    );
  }

  render() {
    const { materialStore, editingMaterial, cancelEdit, t } = this.props;
    const { assignment, materialDetailId } = editingMaterial;
    const name = materialStore.getMaterialName(editingMaterial);
    const title = assignment === 'None' ? `${name} - ${t('Unassigned')}` : `Edit ${name} - ${t(assignment)}`;

    return (
      <div className="material-settings" ref={this.anchor}>
        <Row className="form-header">
          <Col sm={12}>
            <a className="btn-panel-close" onClick={cancelEdit}>x</a>
            <h4>{title}</h4>
          </Col>
        </Row>
        <Tabs
          id={'material-settings-tabs'}
          defaultActiveKey={1}
        >
          <Tab
            eventKey={1}
            title={t('Settings')}
          >
            {this.renderForm()}
          </Tab>
          { assignment !== MaterialType.None && (
            <Tab
              eventKey={2}
              title={t('Audit Log')}
            >
              <AuditLog
                modelId={materialDetailId}
                modelName={`${assignment}MaterialDetail`}
              />
            </Tab>
          )}
        </Tabs>
      </div>
    );
  }
}

export default withTranslation()(inject('materialStore', 'cutoffStore', 'devicesStore', 'settingsStore', 'newAuthStore', 'batchStore', 'locationsStore')(observer(MaterialSettings)));
